import { 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  Paper, 
  Box, 
  IconButton, 
  Chip, 
  Divider,
  TextField,
  MenuItem,
  Button,
  Collapse,
  Avatar,
  alpha,
  useTheme,
  Badge,
  Tooltip,
  LinearProgress
} from "@mui/material";
import { 
  Delete, 
  Edit, 
  Kitchen, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  TrendingUp,
  TrendingDown,
  Event,
  Restaurant,
  Receipt,
  AttachMoney,
  CalendarToday,
  MergeType,
  ExpandMore,
  ExpandLess
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import { format, parseISO, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

// Helper function to merge identical items by product
const mergeItemsByProduct = (items) => {
  const mergedMap = new Map();
  
  items.forEach(item => {
    const key = `${item.productId || item.productName}`;
    
    if (mergedMap.has(key)) {
      const existing = mergedMap.get(key);
      existing.quantity += item.quantity;
      // Keep the original prices (assuming they're the same for same product)
      existing.soldPrice = existing.soldPrice;
      existing.costPrice = existing.costPrice;
    } else {
      mergedMap.set(key, {
        ...item,
        quantity: item.quantity
      });
    }
  });
  
  return Array.from(mergedMap.values());
};

// Helper function to group servings by date with merged items
const groupServingsByDate = (servings, mergeItems = true) => {
  const grouped = new Map();
  
  servings.forEach(serving => {
    const dateKey = format(parseISO(serving.date), 'yyyy-MM-dd');
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, {
        id: `grouped-${dateKey}`,
        date: serving.date,
        items: [],
        originalServings: [],
        dateKey: dateKey
      });
    }
    
    const group = grouped.get(dateKey);
    group.originalServings.push(serving);
    group.items.push(...serving.items);
  });
  
  let groupedArray = Array.from(grouped.values());
  
  // Merge identical items within each grouped date
  if (mergeItems) {
    groupedArray = groupedArray.map(group => ({
      ...group,
      items: mergeItemsByProduct(group.items)
    }));
  }
  
  return groupedArray;
};

export const SavedServingsList = ({ dailyServings, editDailyServing, deleteDailyServing }) => {
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [groupByDate, setGroupByDate] = useState(true);
  const [mergeSameItems, setMergeSameItems] = useState(true); // New state for merging toggle
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Group servings by date if enabled, with optional item merging
  const groupedServings = useMemo(() => {
    if (!groupByDate) {
      // When not grouping by date, still merge items within each individual serving if enabled
      if (mergeSameItems) {
        return dailyServings.map(s => ({ 
          ...s, 
          isGrouped: false, 
          originalServings: [s],
          items: mergeItemsByProduct(s.items)
        }));
      }
      return dailyServings.map(s => ({ ...s, isGrouped: false, originalServings: [s] }));
    }
    return groupServingsByDate(dailyServings, mergeSameItems);
  }, [dailyServings, groupByDate, mergeSameItems]);

  // Get unique months from servings
  const uniqueMonths = Array.from(
    new Set(
      groupedServings.map(serving => {
        const date = new Date(serving.date);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      })
    )
  ).sort().reverse();

  // Filter servings based on selections
  const filteredServings = groupedServings.filter(serving => {
    const servingDate = new Date(serving.date);
    
    if (selectedDate) {
      const selected = new Date(selectedDate);
      return (
        selected.getFullYear() === servingDate.getFullYear() &&
        selected.getMonth() === servingDate.getMonth() &&
        selected.getDate() === servingDate.getDate()
      );
    }
    
    if (selectedMonth) {
      const monthYear = `${servingDate.getFullYear()}-${String(servingDate.getMonth() + 1).padStart(2, '0')}`;
      return monthYear === selectedMonth;
    }
    
    return true;
  });

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setSelectedDate(null);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedMonth('');
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Handle edit for grouped servings
  const handleEditGroup = (serving) => {
    if (serving.originalServings && serving.originalServings.length > 1) {
      alert(`${serving.originalServings.length} services trouvés pour cette date. Veuillez les modifier individuellement.`);
    } else if (serving.originalServings && serving.originalServings.length === 1) {
      editDailyServing(serving.originalServings[0]);
    } else {
      editDailyServing(serving);
    }
  };

  // Handle delete for grouped servings
  const handleDeleteGroup = async (serving) => {
    if (serving.originalServings && serving.originalServings.length > 1) {
      if (window.confirm(`Supprimer tous les ${serving.originalServings.length} services du ${formatDate(serving.date, 'full')} ?`)) {
        for (const originalServing of serving.originalServings) {
          await deleteDailyServing(originalServing.id);
        }
      }
    } else if (serving.originalServings && serving.originalServings.length === 1) {
      deleteDailyServing(serving.originalServings[0].id);
    } else {
      deleteDailyServing(serving.id);
    }
  };

  const formatDate = (dateString, formatType = 'full') => {
    const date = parseISO(dateString);
    switch(formatType) {
      case 'full':
        return format(date, 'EEEE d MMMM yyyy', { locale: fr });
      case 'short':
        return format(date, 'dd/MM/yyyy', { locale: fr });
      case 'day':
        return format(date, 'EEEE', { locale: fr });
      default:
        return format(date, 'd MMMM yyyy', { locale: fr });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <Card sx={{ 
      mb: 4, 
      boxShadow: 3, 
      borderLeft: '4px solid', 
      borderColor: 'secondary.main',
      borderRadius: 2,
      overflow: 'visible'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Kitchen color="action" /> 
              Plans de Service Enregistrés
            </Typography>
            <Tooltip title={groupByDate ? "Désactiver le regroupement" : "Grouper par date"}>
              <Button
                size="small"
                variant={groupByDate ? "contained" : "outlined"}
                onClick={() => setGroupByDate(!groupByDate)}
                startIcon={<MergeType />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                {groupByDate ? "Groupé" : "Non groupé"}
              </Button>
            </Tooltip>
            <Tooltip title={mergeSameItems ? "Désactiver la fusion des articles identiques" : "Fusionner les articles identiques"}>
              <Button
                size="small"
                variant={mergeSameItems ? "contained" : "outlined"}
                onClick={() => setMergeSameItems(!mergeSameItems)}
                startIcon={<MergeType />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  backgroundColor: mergeSameItems ? '#4CAF50' : undefined,
                  '&:hover': {
                    backgroundColor: mergeSameItems ? '#45a049' : undefined
                  }
                }}
              >
                {mergeSameItems ? "Fusionné" : "Non fusionné"}
              </Button>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select
              label="Filtrer par Mois"
              value={selectedMonth}
              onChange={handleMonthChange}
              size="small"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Tous les Mois</MenuItem>
              {uniqueMonths.map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              label="Rechercher par Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={selectedDate || ''}
              onChange={handleDateChange}
              size="small"
              sx={{ minWidth: 160 }}
            />
            
            {(selectedMonth || selectedDate) && (
              <Button 
                size="small" 
                onClick={() => {
                  setSelectedMonth('');
                  setSelectedDate(null);
                }}
                variant="text"
              >
                Effacer les filtres
              </Button>
            )}
          </Box>
        </Box>

        {/* Info banner when merging is active */}
        {mergeSameItems && (
          <Box sx={{ 
            mb: 2, 
            p: 1, 
            bgcolor: alpha('#4CAF50', 0.1), 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <MergeType sx={{ fontSize: 16, color: '#4CAF50' }} />
            <Typography variant="caption" sx={{ color: '#4CAF50' }}>
              Les articles identiques sont automatiquement fusionnés avec leurs quantités cumulées
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {filteredServings.map(serving => {
            const isGrouped = serving.originalServings && serving.originalServings.length > 1;
            const isExpanded = expandedGroups.has(serving.id);
            
            if (isGrouped) {
              return (
                <Grid item xs={12} md={6} key={serving.id}>
                  <GroupedServingItem 
                    serving={serving}
                    isExpanded={isExpanded}
                    onToggleExpand={() => toggleGroupExpanded(serving.id)}
                    onEdit={handleEditGroup}
                    onDelete={handleDeleteGroup}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    mergeSameItems={mergeSameItems}
                  />
                </Grid>
              );
            }
            
            return (
              <Grid item xs={12} md={6} key={serving.id}>
                <ServingItem 
                  serving={serving}
                  editDailyServing={editDailyServing}
                  deleteDailyServing={deleteDailyServing}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  mergeSameItems={mergeSameItems}
                />
              </Grid>
            );
          })}
        </Grid>

        {filteredServings.length === 0 && (
          <Box sx={{ 
            py: 6, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            color: 'text.secondary'
          }}>
            <Kitchen sx={{ fontSize: 60, color: '#B0BEC5' }} />
            <Typography variant="body1">
              Aucun plan de service trouvé
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Component for grouped servings
const GroupedServingItem = ({ 
  serving, 
  isExpanded, 
  onToggleExpand, 
  onEdit, 
  onDelete, 
  formatDate, 
  formatCurrency,
  mergeSameItems 
}) => {
  const theme = useTheme();
  const totalCost = serving.items.reduce((sum, item) => sum + (item.quantity * (item.costPrice || 0)), 0);
  const totalRevenue = serving.items.reduce((sum, item) => sum + (item.quantity * (item.soldPrice || 0)), 0);
  const profit = totalRevenue - totalCost;
  const margin = totalRevenue > 0 ? (profit / totalRevenue * 100) : 0;

  return (
    <Paper elevation={0} sx={{ 
      p: 2, 
      position: 'relative', 
      border: '1px solid', 
      borderColor: 'divider', 
      borderRadius: 2,
      borderLeft: '4px solid',
      borderLeftColor: '#FF9800',
      transition: 'box-shadow 0.3s',
      '&:hover': {
        boxShadow: 3
      },
      backgroundColor: 'rgba(255, 152, 0, 0.02)'
    }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 1 }}>
        <Tooltip title="Modifier">
          <IconButton 
            size="small" 
            onClick={() => onEdit(serving)}
            color="primary"
            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'primary.light' } }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer tout">
          <IconButton 
            size="small" 
            onClick={() => onDelete(serving)}
            color="error"
            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'error.light' } }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isExpanded ? "Réduire" : "Détails"}>
          <IconButton 
            size="small" 
            onClick={onToggleExpand}
            sx={{ bgcolor: 'background.paper' }}
          >
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pr: 8 }}>
        <Badge
          badgeContent={serving.originalServings?.length || 0}
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              height: 18,
              minWidth: 18
            }
          }}
        >
          <Avatar sx={{ 
            backgroundColor: 'rgba(255, 152, 0, 0.1)', 
            color: '#FF9800',
            width: 48,
            height: 48
          }}>
            <MergeType />
          </Avatar>
        </Badge>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            {formatDate(serving.date, 'full')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Chip 
              size="small" 
              label={`${serving.originalServings?.length || 0} services`}
              sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800', height: 20, fontSize: '0.7rem' }}
            />
            <Chip 
              size="small" 
              label={`${serving.items.length} articles`}
              sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', height: 20, fontSize: '0.7rem' }}
            />
            <Chip 
              size="small" 
              label={`${serving.items.reduce((sum, i) => sum + i.quantity, 0)} portions`}
              sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', height: 20, fontSize: '0.7rem' }}
            />
            {mergeSameItems && (
              <Chip 
                size="small" 
                icon={<MergeType sx={{ fontSize: 12 }} />}
                label="Fusionné"
                sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Chiffre d'affaires</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
            {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Coût total</Typography>
          <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
            {formatCurrency(totalCost)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Bénéfice</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: profit >= 0 ? '#4CAF50' : '#f44336' }}>
            {formatCurrency(profit)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Marge</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(margin, 100)}
              sx={{ 
                width: 50,
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: margin >= 30 ? '#4CAF50' : margin >= 20 ? '#FF9800' : '#f44336'
                }
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {margin.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            px: 1,
            mb: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 2 }}>Article</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right', mr: 2 }}>Quantité</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right', mr: 2 }}>CA</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right' }}>Coût</Typography>
          </Box>
          
          {serving.items.map((item, index) => {
            const revenue = item.quantity * (item.soldPrice || 0);
            const cost = item.quantity * (item.costPrice || 0);
            
            return (
              <Box key={index} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}>
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {item.productName}
                  </Typography>
                  {mergeSameItems && item.quantity > 1 && (
                    <Chip 
                      label={`×${item.quantity}`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: alpha('#4CAF50', 0.1),
                        color: '#4CAF50'
                      }} 
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                  {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', mr: 2, color: '#2E7D32' }}>
                  {formatCurrency(revenue)}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', color: '#5F6B7A' }}>
                  {formatCurrency(cost)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 2,
          backgroundColor: 'rgba(255, 152, 0, 0.05)',
          borderRadius: 2
        }}>
          <Typography variant="body2" fontWeight="bold">Bénéfice Total</Typography>
          <Typography variant="body2" fontWeight="bold" color={profit >= 0 ? "success.main" : "error.main"}>
            {formatCurrency(profit)}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

// Component for individual servings
const ServingItem = ({ serving, editDailyServing, deleteDailyServing, formatDate, formatCurrency, mergeSameItems }) => {
  const [expanded, setExpanded] = useState(false);
  
  const safeServing = {
    id: serving.id,
    items: serving.items || [],
    total: serving.total || 0,
    date: serving.date || new Date().toISOString()
  };

  const totalCost = safeServing.items.reduce((sum, item) => sum + (item.quantity * (item.costPrice || 0)), 0);
  const totalRevenue = safeServing.items.reduce((sum, item) => sum + (item.quantity * (item.soldPrice || 0)), 0);
  const profit = totalRevenue - totalCost;
  const margin = totalRevenue > 0 ? (profit / totalRevenue * 100) : 0;

  return (
    <Paper elevation={0} sx={{ 
      p: 2, 
      position: 'relative', 
      border: '1px solid', 
      borderColor: 'divider', 
      borderRadius: 2,
      borderLeft: '4px solid',
      borderLeftColor: 'primary.main',
      transition: 'box-shadow 0.3s',
      '&:hover': {
        boxShadow: 3
      }
    }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 1 }}>
        <Tooltip title="Modifier">
          <IconButton 
            size="small" 
            onClick={() => editDailyServing(serving)}
            color="primary"
            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'primary.light' } }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer">
          <IconButton 
            size="small" 
            onClick={() => deleteDailyServing(serving.id)}
            color="error"
            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'error.light' } }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? "Réduire" : "Détails"}>
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ bgcolor: 'background.paper' }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pr: 8 }}>
        <Avatar sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}>
          <Event />
        </Avatar>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {formatDate(safeServing.date, 'full')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip 
              size="small" 
              label={`${safeServing.items.length} articles`}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            <Chip 
              size="small" 
              label={`${safeServing.items.reduce((sum, i) => sum + i.quantity, 0)} portions`}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {mergeSameItems && (
              <Chip 
                size="small" 
                icon={<MergeType sx={{ fontSize: 12 }} />}
                label="Fusionné"
                sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Chiffre d'affaires</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
            {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Coût</Typography>
          <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
            {formatCurrency(totalCost)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Bénéfice</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: profit >= 0 ? '#4CAF50' : '#f44336' }}>
            {formatCurrency(profit)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Marge</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(margin, 100)}
              sx={{ 
                width: 50,
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: margin >= 30 ? '#4CAF50' : margin >= 20 ? '#FF9800' : '#f44336'
                }
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {margin.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            px: 1,
            mb: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 2 }}>Article</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right', mr: 2 }}>Quantité</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right', mr: 2 }}>CA</Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, textAlign: 'right' }}>Coût</Typography>
          </Box>
          
          {safeServing.items.map((item, index) => {
            const revenue = item.quantity * (item.soldPrice || 0);
            const cost = item.quantity * (item.costPrice || 0);
            
            return (
              <Box key={index} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}>
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {item.productName}
                  </Typography>
                  {mergeSameItems && item.quantity > 1 && (
                    <Chip 
                      label={`×${item.quantity}`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        bgcolor: alpha('#4CAF50', 0.1),
                        color: '#4CAF50'
                      }} 
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                  {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', mr: 2, color: '#2E7D32' }}>
                  {formatCurrency(revenue)}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', color: '#5F6B7A' }}>
                  {formatCurrency(cost)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};